(() => {
  if (window.jbWishlistReady) return;
  window.jbWishlistReady = true;
  const selector = '[data-jb-save], .jb-card-save';
  const prefix = 'jb-saved-';
  const detailsKey = 'jb-wishlist-details-v1';
  const saved = new Set();
  const details = new Map();
  const volatile = new Map();
  const pending = new Map();
  const errors = new Map();
  let persistent = true;
  let panel, dialog, labels, opener, toastTimer, toastMotion, toastPositionFrame, openMotion, closeMotion;
  const heartMotions = new WeakMap();
  const validHandle = handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle);
  const handleFor = button => button.dataset.jbSave || button.closest('jb-card-media')?.dataset.handle;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  function readStorage() {
    try {
      const next = new Set();
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix) && validHandle(key.slice(prefix.length)) && localStorage.getItem(key) === 'true') next.add(key.slice(prefix.length));
      }
      let stored = {};
      try { stored = JSON.parse(localStorage.getItem(detailsKey) || '{}'); } catch {}
      if (stored && typeof stored === 'object' && !Array.isArray(stored)) Object.entries(stored).forEach(([handle, item]) => {
        if (validHandle(handle) && item && typeof item.title === 'string') details.set(handle, item);
      });
      saved.clear();
      next.forEach(handle => saved.add(handle));
      volatile.forEach((selected, handle) => selected ? saved.add(handle) : saved.delete(handle));
      persistent = volatile.size === 0;
    } catch { persistent = false; }
  }
  function storeDetails() {
    try { localStorage.setItem(detailsKey, JSON.stringify(Object.fromEntries([...details].filter(([handle]) => saved.has(handle))))); } catch {}
  }
  function safeUrl(value, kind) {
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      if (kind === 'product' && (url.origin !== location.origin || !url.pathname.includes('/products/'))) return '';
      if (kind === 'image' && url.origin !== location.origin && url.hostname !== 'cdn.shopify.com') return '';
      return url.href;
    } catch { return ''; }
  }
  function productUrl(handle) {
    const root = panel?.dataset.rootUrl || '/';
    return `${root.replace(/\/?$/, '/') }products/${encodeURIComponent(handle)}?view=jb`;
  }
  function capture(button) {
    const handle = handleFor(button);
    if (!validHandle(handle) || !button.dataset.jbWishlistTitle) return;
    const item = { title: button.dataset.jbWishlistTitle, url: safeUrl(button.dataset.jbWishlistUrl || productUrl(handle), 'product'), image: safeUrl(button.dataset.jbWishlistImage || '', 'image') };
    details.set(handle, item);
  }
  function sync() {
    document.querySelectorAll(selector).forEach(button => {
      const handle = handleFor(button);
      if (!validHandle(handle)) return;
      capture(button);
      const selected = saved.has(handle);
      const label = selected ? labels?.remove : labels?.add;
      if (button.getAttribute('aria-pressed') !== String(selected)) button.setAttribute('aria-pressed', String(selected));
      if (label) { button.setAttribute('aria-label', label); button.title = label; }
    });
    document.querySelectorAll('[data-jb-wishlist-count]').forEach(node => { node.textContent = String(saved.size); });
    const note = panel?.querySelector('.jb-wishlist-storage');
    if (note) note.textContent = persistent ? labels.storage : labels.storage_unavailable;
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }
  function render() {
    if (!dialog?.open) return;
    const list = panel.querySelector('[data-jb-wishlist-items]');
    const focusedHandle = document.activeElement?.closest('[data-wishlist-handle]')?.dataset.wishlistHandle;
    const focusAction = document.activeElement?.dataset.jbWishlistRemove !== undefined ? 'remove' : document.activeElement?.dataset.jbWishlistRetry !== undefined ? 'retry' : '';
    const nodes = [...saved].reverse().map(handle => {
      const item = details.get(handle);
      const row = element('li', 'jb-wishlist-item');
      row.dataset.wishlistHandle = handle;
      const imageLink = element('a', 'jb-wishlist-item__image');
      imageLink.href = safeUrl(item?.url || productUrl(handle), 'product') || productUrl(handle);
      imageLink.setAttribute('aria-label', item?.title || labels.creation);
      if (item?.image && safeUrl(item.image, 'image')) {
        const img = element('img');
        img.src = safeUrl(item.image, 'image'); img.alt = ''; img.width = 120; img.height = 120; img.loading = 'lazy';
        imageLink.append(img);
      } else imageLink.append(element('span', '', 'JB'));
      row.append(imageLink);
      const copy = element('div', 'jb-wishlist-item__copy');
      const title = element('a', 'jb-wishlist-item__title', item?.title || labels.loading);
      title.href = imageLink.href; copy.append(title);
      if (errors.has(handle)) {
        copy.append(element('p', 'jb-wishlist-item__error', errors.get(handle) === 404 ? labels.unavailable : labels.load_error));
        if (errors.get(handle) !== 404) {
          const retry = element('button', 'jb-wishlist-item__link', labels.retry);
          retry.type = 'button'; retry.dataset.jbWishlistRetry = handle; copy.append(retry);
        }
      } else {
        const open = element('a', 'jb-wishlist-item__link', labels.open);
        open.href = imageLink.href; copy.append(open);
      }
      const remove = element('button', 'jb-wishlist-item__remove', labels.remove);
      remove.type = 'button'; remove.dataset.jbWishlistRemove = handle;
      remove.setAttribute('aria-label', `${labels.remove}: ${item?.title || labels.creation}`);
      copy.append(remove); row.append(copy); return row;
    });
    list.replaceChildren(...nodes);
    panel.querySelector('[data-jb-wishlist-empty]').hidden = saved.size > 0;
    if (focusedHandle && focusAction) {
      const row = [...list.children].find(node => node.dataset.wishlistHandle === focusedHandle) || list.firstElementChild;
      (row?.querySelector(focusAction === 'remove' ? '[data-jb-wishlist-remove]' : '[data-jb-wishlist-retry]') || panel.querySelector('[data-jb-wishlist-close]')).focus({ preventScroll: true });
    }
  }
  async function loadDetail(handle) {
    if (pending.has(handle)) return pending.get(handle);
    const task = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const root = panel?.dataset.rootUrl || '/';
        const response = await fetch(`${root.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}.js`, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) { const error = new Error('Product unavailable'); error.status = response.status; throw error; }
        const product = await response.json();
        if (!product.title) throw new Error('Product unavailable');
        details.set(handle, { title: product.title, url: productUrl(handle), image: safeUrl(product.featured_image || '', 'image') });
        errors.delete(handle); storeDetails();
      } catch (error) { errors.set(handle, error.status === 404 ? 404 : 0); }
      finally { clearTimeout(timer); pending.delete(handle); if (saved.has(handle)) render(); }
    })();
    pending.set(handle, task); return task;
  }
  async function loadMissing() {
    const queue = [...saved].filter(handle => !details.has(handle) && !errors.has(handle));
    await Promise.all(Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length) await loadDetail(queue.shift());
    }));
  }
  function positionToast() {
    const toast = panel?.querySelector('[data-jb-wishlist-toast]');
    if (!toast || toast.hidden) return;
    const bar = document.querySelector('.jb-purchase-bar.is-visible');
    const barTop = bar && getComputedStyle(bar).visibility !== 'hidden' ? bar.getBoundingClientRect().top : innerHeight;
    const offset = matchMedia('(max-width: 767px)').matches ? Math.max(0, innerHeight - barTop) : 0;
    toast.style.setProperty('--jb-wishlist-bottom-offset', `${Math.ceil(offset)}px`);
  }
  function scheduleToastPosition() {
    cancelAnimationFrame(toastPositionFrame);
    toastPositionFrame = requestAnimationFrame(positionToast);
  }
  function dismissToast(immediate = false) {
    clearTimeout(toastTimer);
    const toast = panel?.querySelector('[data-jb-wishlist-toast]');
    toastMotion?.cancel(); toastMotion = null;
    if (!toast || toast.hidden) return;
    if (immediate || reduced() || !toast.animate) { toast.hidden = true; return; }
    const motion = toast.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(8px)' }], { duration: 180, easing: 'ease-in', fill: 'both' });
    toastMotion = motion;
    const finish = () => {
      if (toastMotion !== motion) return;
      toast.hidden = true; motion.cancel(); toastMotion = null;
    };
    motion.finished.then(finish, finish);
  }
  function notify(message) {
    const toast = panel?.querySelector('[data-jb-wishlist-toast]');
    if (!toast) return;
    panel.querySelector('[data-jb-wishlist-message]').textContent = message;
    if (dialog?.open) { panel.querySelector('[data-jb-wishlist-live]').textContent = message; return; }
    const entering = toast.hidden;
    toastMotion?.cancel(); toastMotion = null;
    toast.hidden = false; positionToast();
    if (entering && !reduced() && toast.animate) {
      const motion = toast.animate([{ opacity: .9, transform: 'translateY(3px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 200, easing: 'cubic-bezier(.22,.61,.36,1)' });
      toastMotion = motion;
      const finish = () => { if (toastMotion === motion) { motion.cancel(); toastMotion = null; } };
      motion.finished.then(finish, finish);
    }
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { if (!toast.contains(document.activeElement)) dismissToast(); }, 4000);
  }
  function feedback(button, selected) {
    const icon = button?.querySelector('.jb-wishlist-icon');
    if (!icon) return;
    heartMotions.get(icon)?.forEach(motion => motion.cancel());
    heartMotions.delete(icon);
    if (reduced() || !icon.animate) return;
    const finalShape = icon.querySelector(selected ? '.jb-wishlist-icon__filled' : '.jb-wishlist-icon__outline');
    if (!finalShape) return;
    const motions = [finalShape.animate([{ opacity: .35 }, { opacity: 1 }], { duration: 200, easing: 'cubic-bezier(.22,.61,.36,1)' })];
    heartMotions.set(icon, motions);
    Promise.all(motions.map(motion => motion.finished)).then(() => {
      if (heartMotions.get(icon) !== motions) return;
      motions.forEach(motion => motion.cancel()); heartMotions.delete(icon);
    }, () => {});
  }
  function change(handle, selected, button) {
    if (!validHandle(handle)) return;
    if (button) capture(button);
    if (selected) saved.add(handle); else saved.delete(handle);
    try { localStorage.setItem(prefix + handle, String(selected)); volatile.delete(handle); persistent = volatile.size === 0; } catch { volatile.set(handle, selected); persistent = false; }
    storeDetails(); sync(); render();
    const message = selected ? (persistent ? labels.saved : labels.temporary) : (persistent ? labels.removed : labels.temporary_removed);
    notify(message);
    const status = button?.closest('jb-card-media')?.querySelector('[role="status"]') || button?.closest('.jb-product-experience')?.querySelector('.jb-save-status');
    if (status) status.textContent = message;
    feedback(button, selected);
  }
  function open(button) {
    if (!dialog) return;
    closeMotion?.cancel(); closeMotion = null;
    openMotion?.cancel(); openMotion = null;
    opener = button; dismissToast(true);
    readStorage(); sync();
    if (!dialog.open) dialog.showModal();
    document.querySelectorAll('[data-jb-wishlist-open]').forEach(node => node.setAttribute('aria-expanded', 'true'));
    render(); loadMissing();
    if (!reduced() && dialog.animate) {
      const motion = dialog.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], { duration: 350, easing: 'cubic-bezier(.4,.9,.3,1)' });
      openMotion = motion;
      const finish = () => { if (openMotion === motion) { motion.cancel(); openMotion = null; } };
      motion.finished.then(finish, finish);
    }
    document.dispatchEvent(new CustomEvent('panel:open'));
  }
  function closed() {
    if (dialog?.open) return;
    openMotion?.cancel(); openMotion = null;
    closeMotion?.cancel(); closeMotion = null;
    document.querySelectorAll('[data-jb-wishlist-open]').forEach(node => node.setAttribute('aria-expanded', 'false'));
    const target = opener?.isConnected && opener.getClientRects().length ? opener : document.querySelector('.jb-wishlist-launcher');
    target?.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('panel:closed'));
  }
  function close() {
    if (!dialog?.open || closeMotion) return;
    openMotion?.cancel(); openMotion = null;
    if (reduced() || !dialog.animate) { dialog.close(); return; }
    const motion = dialog.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], { duration: 250, easing: 'ease-in' });
    closeMotion = motion;
    const finish = () => { if (closeMotion === motion) { closeMotion = null; dialog.close(); } };
    motion.finished.then(finish, finish);
  }
  function initialize() {
    const next = document.querySelector('jb-wishlist-panel');
    if (!next) return;
    if (panel !== next) {
      panel = next; dialog = panel.querySelector('dialog');
      if (panel.parentElement !== document.body) document.body.append(panel);
      try { labels = JSON.parse(panel.querySelector('[data-jb-wishlist-labels]').textContent); } catch { return; }
      dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
      dialog.addEventListener('close', closed);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      });
    }
    sync();
  }
  document.addEventListener('click', event => {
    if (!panel || !labels) return;
    const target = event.target.closest?.('button');
    if (!target || target.disabled) return;
    if (target.matches('[data-jb-wishlist-open]')) { event.preventDefault(); open(target); }
    else if (target.matches('[data-jb-wishlist-close]')) close();
    else if (target.matches('[data-jb-wishlist-dismiss]')) dismissToast();
    else if (target.matches('[data-jb-wishlist-remove]')) change(target.dataset.jbWishlistRemove, false);
    else if (target.matches('[data-jb-wishlist-retry]')) { errors.delete(target.dataset.jbWishlistRetry); loadDetail(target.dataset.jbWishlistRetry); }
    else if (target.matches(selector)) { event.preventDefault(); const handle = handleFor(target); change(handle, !saved.has(handle), target); }
  });
  let scheduled = false;
  new MutationObserver(records => {
    if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.(`${selector},jb-wishlist-panel`) || node.querySelector?.(`${selector},jb-wishlist-panel`))))) return;
    if (!scheduled) { scheduled = true; queueMicrotask(() => { scheduled = false; initialize(); }); }
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('storage', event => {
    if (event.key !== null && event.key !== detailsKey && !event.key.startsWith(prefix)) return;
    readStorage(); sync(); render(); loadMissing();
  });
  window.addEventListener('pageshow', () => { readStorage(); initialize(); render(); });
  window.addEventListener('resize', scheduleToastPosition, { passive: true });
  window.addEventListener('scroll', scheduleToastPosition, { passive: true });
  readStorage(); initialize();
})();

